import { memo, useEffect, useRef, useState } from "react";
import { AnimatePresence, m, useReducedMotion } from "framer-motion";

/**
 * The exact seed content FelixChat writes into the placeholder assistant
 * message the moment a stream starts, before any token arrives. The chat
 * bubble renders <FelixThinking/> in place of this sentinel and the first
 * streamed token overwrites the content, so the indicator disappears on its
 * own. Keep this as the single source of truth for both the seed and the
 * render-time check.
 */
export const FELIX_THINKING_SEED = "*Thinking it through…*";

/**
 * FelixThinking — the "Felix is thinking" status wheel.
 *
 * A small conic-gradient spinner + a rotating pithy PM-ism ("Triaging…",
 * "Naming the risk…", "Weighing the trade-off…") — Claude-Code-style, but in
 * the voice of an AI Program Manager. It mounts while Felix is composing and
 * vanishes the instant his first token streams in (the caller swaps it out by
 * replacing the seed message content — see FELIX_THINKING_SEED).
 *
 * Voice curated to sound like a competent PM narrating real SDLC moves, never
 * filler and never at the user's expense. The first PHRASE is always drawn from
 * the "safe" head of the list (the highest-frequency, first-impression slot),
 * so it can never open on something flippant.
 */

// 54 PM-isms. Order matters: SAFE_HEAD (the first 8) are the unimpeachable,
// most professional ones — they open every turn, so they must never read as
// glib. The rest add variety across ceremony, risk/estimation, stakeholder,
// and decisive-operator registers.
export const FELIX_PMISMS: readonly string[] = [
  // — safe head (first impression) —
  "Weighing the trade-off…",
  "Naming the risk…",
  "Aligning stakeholders…",
  "Charting the critical path…",
  "Pressure-testing scope…",
  "Clearing blockers…",
  "Triaging…",
  "Sequencing the work…",
  // — ceremony & backlog —
  "Grooming the backlog…",
  "Story-pointing…",
  "Refining stories…",
  "Decomposing epics…",
  "Splitting the story…",
  "Timeboxing…",
  "Estimating velocity…",
  "Spiking the unknown…",
  "Re-prioritizing…",
  "Pulling the top story…",
  "Committing the sprint…",
  "Burning down…",
  "Swarming the blocker…",
  // — risk, estimation & trade-off —
  "Right-sizing the lift…",
  "Padding the estimate…",
  "Discounting the happy path…",
  "Booking contingency…",
  "Hedging the timeline…",
  "Ranking the risks…",
  "Pricing the unknowns…",
  "Costing the change…",
  "Quantifying the doubt…",
  "Assuming the worst…",
  "De-risking…",
  // — stakeholder & alignment —
  "Reading the room…",
  "Chasing sign-off…",
  "Closing the loop…",
  "Herding approvers…",
  "Building consensus…",
  "Brokering alignment…",
  "Threading interests…",
  "Bridging camps…",
  "Landing buy-in…",
  // — decisive operator —
  "Interrogating the ask…",
  "Baselining scope…",
  "Killing scope creep…",
  "Poking holes…",
  "Deconflicting…",
  "Herding personas…",
  "Drawing the line…",
  "Tightening acceptance…",
  "Chasing green…",
  "Calling the shot…",
  "Sharpening the ask…",
  "Reconciling dependencies…",
  "Connecting the dots…",
];

export const SAFE_HEAD = 8;
const ROTATE_MS = 2600;
const COUNTER_AFTER_S = 8;

/** Random next phrase index, never an immediate repeat. Exported for tests. */
export function pickNext(prev: number): number {
  // Random from the full set, never an immediate repeat.
  if (FELIX_PMISMS.length < 2) return 0;
  let n = prev;
  while (n === prev) n = Math.floor(Math.random() * FELIX_PMISMS.length);
  return n;
}

/**
 * Row f5098ee9 (founder 6363652362): what a held round says while Felix works.
 * After an action tool runs, his words wait for the round's end (the honesty
 * rule), so the bubble used to sit on a static italic "Working on it…" for
 * 11-20 s (MEASURED 10-23: 27 of 50 nightly replies landed all at once). The
 * person reads what he is doing; tool names stay hidden (Bob 2026-06-20).
 */
export const HELD_ROUND_STATUS: Readonly<Record<string, string>> = {
  answer_qa_question: "Felix is recording that and checking the plan…",
  record_open_item: "Felix is recording that and checking the plan…",
  record_decision: "Felix is recording that and checking the plan…",
  start_qa_inline: "Felix is setting up the questions…",
  propose_approval: "Felix is preparing that for your approval…",
  propose_phase_advance: "Felix is checking whether the phase is ready…",
  update_change_request: "Felix is updating the change and its impact…",
  create_change_request: "Felix is writing up the change…",
  analyze_change_impact: "Felix is working out the impact…",
};

/** The status for a round's tools: the last one that names a held round. */
export function heldRoundStatus(tools: readonly (string | undefined)[]): string | undefined {
  for (let i = tools.length - 1; i >= 0; i--) {
    const status = tools[i] ? HELD_ROUND_STATUS[tools[i] as string] : undefined;
    if (status) return status;
  }
  return undefined;
}

/**
 * ``label``: a fixed status in place of the rotating PM-isms, shown while a
 * tool runs and its round's words are held (row f5098ee9). The spinner and the
 * elapsed counter stay, so the person sees he is still working.
 */
function FelixThinkingImpl({ label }: { label?: string } = {}) {
  const reduce = useReducedMotion();
  // Open on a safe-head phrase so the first impression is always professional.
  const [idx, setIdx] = useState(() => Math.floor(Math.random() * SAFE_HEAD));
  const [elapsed, setElapsed] = useState(0);
  const idxRef = useRef(idx);
  idxRef.current = idx;

  useEffect(() => {
    const rotate = window.setInterval(() => {
      setIdx((cur) => pickNext(cur));
    }, ROTATE_MS);
    const tick = window.setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => {
      window.clearInterval(rotate);
      window.clearInterval(tick);
    };
  }, []);

  const phrase = FELIX_PMISMS[idx] ?? FELIX_PMISMS[0];

  return (
    <div
      role="status"
      aria-live="polite"
      aria-atomic="true"
      className="flex items-center gap-2 py-0.5 text-sm font-medium tracking-tight text-slate-600 dark:text-slate-300"
    >
      {/* Stable label for assistive tech — the rotating phrase is flavour. */}
      <span className="sr-only">Felix is working</span>

      {/* Spinner: a conic-gradient ring in the app's brand→violet accent, with
          a surface-matched core so it appears to float on light and dark. */}
      {reduce ? (
        // prefers-reduced-motion: a static accent dot, no rotation or pulse.
        <span
          aria-hidden="true"
          className="inline-block h-3 w-3 shrink-0 rounded-full bg-brand-600"
        />
      ) : (
        <m.span
          aria-hidden="true"
          className="relative inline-block h-4 w-4 shrink-0"
          animate={{ rotate: 360 }}
          transition={{ duration: 0.9, ease: "linear", repeat: Infinity }}
        >
          <span
            className="absolute inset-0 rounded-full"
            style={{
              background:
                "conic-gradient(from 0deg, transparent 8%, var(--color-brand-600) 55%, var(--color-accent-violet) 100%)",
              WebkitMask:
                "radial-gradient(farthest-side, transparent calc(100% - 2px), #000 calc(100% - 2px))",
              mask: "radial-gradient(farthest-side, transparent calc(100% - 2px), #000 calc(100% - 2px))",
            }}
          />
        </m.span>
      )}

      {/* Rotating phrase — cross-fades in place, fixed min-width so the chat
          column never reflows as the words change length. */}
      <span className="relative inline-flex min-w-[11rem] items-center">
        <AnimatePresence mode="wait" initial={false}>
          <m.span
            key={label ?? idx}
            initial={reduce ? { opacity: 0 } : { opacity: 0, y: 6 }}
            animate={reduce ? { opacity: 1 } : { opacity: 1, y: 0 }}
            exit={reduce ? { opacity: 0 } : { opacity: 0, y: -6 }}
            transition={{ duration: reduce ? 0.12 : 0.22, ease: [0.22, 1, 0.36, 1] }}
            className={label ? "whitespace-normal" : "whitespace-nowrap"}
          >
            {label ?? phrase}
          </m.span>
        </AnimatePresence>
      </span>

      {/* Elapsed seconds — appears only once a turn is genuinely running, so
          quick replies stay clean. Muted + tabular so it doesn't jitter. */}
      {elapsed >= COUNTER_AFTER_S && (
        <span
          aria-hidden="true"
          className="tabular-nums text-xs text-slate-600 dark:text-slate-300"
        >
          · {elapsed}s
        </span>
      )}
    </div>
  );
}

export const FelixThinking = memo(FelixThinkingImpl);
export default FelixThinking;
